import { useEffect, useMemo, useState } from "react";
import { FlipHorizontal2 } from "lucide-react";
import { anchors, backViewAreas } from "@/content/anchors";
import { bodyAreas } from "@/content";
import { BLOBS, LIMBS, TORSO_PROFILE } from "./figureData";
import Hotspot from "./Hotspot";

// 2.5D fallback: the same figure projected to front/back SVG views.
const S = 205;
const px = (x) => 100 + x * S;
const py = (y) => 8 + (1.82 - y) * S;
const TORSO_PATH = (() => {
  const right = TORSO_PROFILE.map(([r, y]) => `${px(r).toFixed(1)} ${py(y).toFixed(1)}`);
  const left = [...TORSO_PROFILE].reverse().map(([r, y]) => `${px(-r).toFixed(1)} ${py(y).toFixed(1)}`);
  return `M${[...right, ...left].join(" L")} Z`;
})();

const Silhouette = ({ back = false, id }) => (
  <svg viewBox="0 0 200 400" className="absolute inset-0 h-full w-full" aria-hidden="true">
    <defs>
      <linearGradient id={id} x1="40" x2="160" y1="0" y2="0" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#CDBBA0" />
        <stop offset="0.42" stopColor="#F1E7D8" />
        <stop offset="0.6" stopColor="#E9DCC8" />
        <stop offset="1" stopColor="#C8B598" />
      </linearGradient>
    </defs>
    <ellipse cx="100" cy={py(0)} rx="46" ry="7" fill="rgba(43,59,48,0.16)" />
    <g fill={`url(#${id})`} stroke={`url(#${id})`} strokeLinecap="round">
      {LIMBS.filter((l) => l.a[2] === l.b[2]).map((l, i) => (
        <line key={i} x1={px(l.a[0])} y1={py(l.a[1])} x2={px(l.b[0])} y2={py(l.b[1])} strokeWidth={l.r * 2 * S} />
      ))}
      {[-1, 1].map((s) => <ellipse key={s} cx={px(0.1 * s)} cy={py(0.05)} rx="9.5" ry="7" stroke="none" />)}
      <path d={TORSO_PATH} stroke="none" />
      {BLOBS.map((b, i) => <ellipse key={i} cx={px(b.pos[0])} cy={py(b.pos[1])} rx={b.scale[0] * S} ry={b.scale[1] * S} stroke="none" />)}
    </g>
    {back && <path d={`M100 ${py(1.46)} L100 ${py(1.0)}`} stroke="#B9A588" strokeWidth="1" strokeLinecap="round" opacity=".6" />}
  </svg>
);

export const BodyPoster = () => (
  <div className="relative mx-auto aspect-[1/2] h-full opacity-70" data-testid="body-poster" aria-hidden="true">
    <Silhouette id="np-grad-poster" />
  </div>
);

const Face = ({ back, active, onSelect, hidden }) => {
  const slugs = back ? backViewAreas : Object.keys(anchors).filter((k) => anchors[k].normal[2] > -0.2);
  return (
    <div className={`flat-face absolute inset-0 ${back ? "is-back" : ""}`} inert={hidden ? true : undefined} aria-hidden={hidden}>
      <Silhouette back={back} id={back ? "np-grad-back" : "np-grad-front"} />
      {slugs.map((slug) => {
        const [x, y] = anchors[slug].pos;
        const area = bodyAreas.find((a) => a.slug === slug);
        return (
          <Hotspot
            key={slug}
            area={area}
            active={active === slug}
            onSelect={onSelect}
            testId={`body-flat-hotspot-${back ? "back" : "front"}-${slug}`}
            className="hs-flat"
            style={{ left: `${(px(back ? -x : x) / 200) * 100}%`, top: `${(py(y) / 400) * 100}%` }}
          />
        );
      })}
    </div>
  );
};

export default function BodyFlat({ active, onSelect }) {
  const [back, setBack] = useState(false);
  const frontSlugs = useMemo(() => Object.keys(anchors).filter((k) => anchors[k].normal[2] > -0.2), []);
  useEffect(() => {
    if (!active) return;
    if (back && !backViewAreas.includes(active)) setBack(false);
    if (!back && !frontSlugs.includes(active)) setBack(true);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="absolute inset-0 flex items-center justify-center p-6" data-testid="body-flat">
      <div className="flat-flipper relative aspect-[1/2] h-full">
        <div className={`flat-inner absolute inset-0 ${back ? "is-flipped" : ""}`}>
          <Face back={false} active={active} onSelect={onSelect} hidden={back} />
          <Face back active={active} onSelect={onSelect} hidden={!back} />
        </div>
      </div>
      <button
        type="button"
        onClick={() => setBack((b) => !b)}
        aria-pressed={back}
        data-testid="body-flat-flip"
        className="absolute right-4 top-4 z-[200] inline-flex h-11 items-center gap-2 rounded-full bg-cream/90 px-4 text-sm font-semibold text-forest ring-1 ring-line backdrop-blur hover:bg-white"
      >
        <FlipHorizontal2 className="h-4 w-4" aria-hidden="true" /> {back ? "Visa framsida" : "Visa baksida"}
      </button>
    </div>
  );
}
